import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, HeartPulse, Droplet, Calculator, MessageCircle, Wind, Lock, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { isPremium, inTrial, startProCheckout } from "../lib/premium";

const ITEMS = [
  { key: "clinfun", label: "ClinFun", desc: "Diplomas y etiquetas", Icon: Sparkles, color: "#e05a9c" },
  { key: "clinbreath", label: "ClinBreath", desc: "Asistente ventilatorio", Icon: Wind, color: "#2563eb", gift: "has_clinbreath" },
  { key: "clinpulse", label: "ClinPulse", desc: "Infusiones pediátricas", Icon: HeartPulse, color: "#ef4444", gift: "has_clinpulse" },
  { key: "cliniv", label: "ClinIV", desc: "Cálculo de sueros", Icon: Droplet, color: "#0ea5e9" },
  { key: "calc", label: "Calculadoras", desc: "Dosis y fórmulas", Icon: Calculator, color: "#d1745a" },
  { key: "oci", label: "Asistente Oci", desc: "Pregúntale a Oci", Icon: MessageCircle, color: "#00B4CB", premiumOnly: true },
];

export default function ClinGo() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const pro = isPremium(user);
  const trial = inTrial(user);
  // Oci (premiumOnly) es solo Pro (NO se incluye en la prueba). ClinPulse/ClinBreath: Pro, prueba o regalo.
  const isLocked = (it) => {
    if (it.premiumOnly) return !pro;
    if (it.gift) return !(pro || trial || user?.[it.gift]);
    return false;
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true });
    return () => window.removeEventListener("scroll", close);
  }, [open]);

  const pick = (it) => {
    setOpen(false);
    if (isLocked(it)) {
      setTimeout(() => startProCheckout(), 150);
      return;
    }
    setTimeout(() => window.dispatchEvent(new CustomEvent("clingo-open", { detail: it.key })), 60);
  };

  return (
    <div className="md:hidden">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="clingo-backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[69] bg-brand-ink/40 backdrop-blur-[2px]"
          />
        )}
      </AnimatePresence>

      <div className="fixed bottom-5 right-4 z-[71] flex flex-col items-end gap-3">
        <AnimatePresence>
          {open && (
            <motion.ul
              data-testid="clingo-menu"
              initial="hidden" animate="show" exit="hidden"
              variants={{ show: { transition: { staggerChildren: 0.05 } }, hidden: { transition: { staggerChildren: 0.03, staggerDirection: -1 } } }}
              className="flex flex-col items-end gap-2.5"
            >
              {ITEMS.map((it) => {
                const locked = isLocked(it);
                return (
                <motion.li
                  key={it.key}
                  variants={{ hidden: { opacity: 0, x: 20, scale: 0.8 }, show: { opacity: 1, x: 0, scale: 1 } }}
                >
                  <button
                    data-testid={`clingo-item-${it.key}`}
                    onClick={() => pick(it)}
                    className="flex items-center gap-3 bg-white rounded-full pl-4 pr-2 py-2 shadow-lg active:scale-95 transition-transform"
                  >
                    <div className="text-right leading-tight">
                      <p className="font-outfit font-bold text-sm text-brand-ink flex items-center gap-1 justify-end">
                        {it.label}
                        {locked && <Lock size={11} className="text-brand-ink/40" />}
                      </p>
                      <p className="text-[11px] text-brand-ink/50">{locked ? "Función Pro · toca para desbloquear" : it.desc}</p>
                    </div>
                    <span className="relative w-11 h-11 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: it.color, opacity: locked ? 0.5 : 1 }}>
                      <it.Icon size={20} />
                      {locked && (
                        <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-brand-ink text-white flex items-center justify-center shadow">
                          <Lock size={11} />
                        </span>
                      )}
                    </span>
                  </button>
                </motion.li>
                );
              })}
            </motion.ul>
          )}
        </AnimatePresence>

        <motion.button
          data-testid="clingo-fab"
          onClick={() => setOpen((o) => !o)}
          whileTap={{ scale: 0.9 }}
          className={`relative w-16 h-16 rounded-full flex items-center justify-center overflow-visible ${open ? "bg-gradient-to-br from-teal-500 to-emerald-500 shadow-xl shadow-teal-500/40" : "drop-shadow-lg"}`}
          aria-label="ClinGo · herramientas"
        >
          {!open && (
            <motion.span
              className="absolute inset-1 rounded-full bg-sky-400/30"
              animate={{ scale: [1, 1.25, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            />
          )}
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} className="text-white">
                <X size={26} />
              </motion.span>
            ) : (
              <motion.img
                key="oci" src="/oci/clingo.png" alt="ClinGo" draggable={false}
                initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.6, opacity: 0 }}
                className="relative w-[4.2rem] h-[4.2rem] object-contain drop-shadow"
              />
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </div>
  );
}
